import './Home.scss'

import { History, Location } from 'history'
import { useApi } from 'lib/client/api'
import GenericError from 'lib/components/ErrorPages/GenericError'
import Header from 'lib/components/Header/Header'
import AdminContext from 'lib/context/AdminContext'
import AuthContext from 'lib/context/AuthContext'
import SubContext from 'lib/context/SubContext'
import { events, track } from 'lib/util/analytics'
import compareCaseInsensitive from 'lib/util/compareCaseInsensitive'
import { reportError } from 'lib/util/errorHandler'
import openInNewTab from 'lib/util/openInNewTab'
import { PATHS, QUERY_KEYS, QUERY_VALUES, setParamInUrl, UPCOMING_WEBINAR_SIGN_UP_ID } from 'lib/util/routing'
import { isLoggedIn } from 'lib/util/self'
import Footer from 'pages/Home/Footer/Footer'
import ProductCategory from 'pages/Home/ProductCategory'
import RecordedWebinarType from 'pages/Home/RecordedWebinarType'
import Section from 'pages/Home/Section'
import Series from 'pages/Home/Series'
import { getSkillLevelByQueryValue } from 'pages/Home/SkillLevel'
import { getStartTimeByQueryValue } from 'pages/Home/StartTime'
import UpcomingWebinarType from 'pages/Home/UpcomingWebinarType'
import VideoType from 'pages/Home/VideoType'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import ProductCategoryFilter from './ProductCategoryFilter/ProductCategoryFilter'
import { getRecordedWebinarSortByQueryValue } from './RecordedWebinarSort'
import RecordedWebinarsSection from './RecordedWebinarsSection/RecordedWebinarsSection'
import useSearchIndex from './search/useSearchIndex'
import KnownSeries from './SeriesFilter/KnownSeries'
import TabGroup from './TabGroup/TabGroup'
import UpcomingWebinarsSection from './UpcomingWebinarsSection/UpcomingWebinarsSection'
import VideosSection from './VideosSection/VideosSection'

function sortSeries(seriesList: Series[]) {
  const SERIES_ORDER = [KnownSeries.QuickTips, KnownSeries.Essentials, KnownSeries.GettingStarted]

  function findIndex(series: Series) {
    return SERIES_ORDER.indexOf(SERIES_ORDER.find(titlePrefix => series.title.toLowerCase().startsWith(titlePrefix)))
  }

  return seriesList.sort((a, b) => findIndex(b) - findIndex(a) || compareCaseInsensitive(a.title, b.title))
}

export interface HomeProps extends RouteComponentProps {
  section: Section
}

function getCurrentProductCategory(queryValue: string, history: History, location: Location) {
  if (queryValue === QUERY_VALUES.rise) {
    localStorage.setItem(QUERY_KEYS.category, QUERY_VALUES.rise)

    return ProductCategory.Rise
  }
  if (queryValue === QUERY_VALUES.storyline) {
    localStorage.setItem(QUERY_KEYS.category, QUERY_VALUES.storyline)

    return ProductCategory.Storyline
  }
  if (queryValue === QUERY_VALUES.more) {
    localStorage.setItem(QUERY_KEYS.category, QUERY_VALUES.more)

    return ProductCategory.More
  }

  const storedCategory = localStorage.getItem(QUERY_KEYS.category)
  if (storedCategory === QUERY_VALUES.rise) {
    setParamInUrl(QUERY_KEYS.category, QUERY_VALUES.rise, history, location)

    return ProductCategory.Rise
  }
  if (storedCategory === QUERY_VALUES.storyline) {
    setParamInUrl(QUERY_KEYS.category, QUERY_VALUES.storyline, history, location)

    return ProductCategory.Storyline
  }
  if (storedCategory === QUERY_VALUES.more) {
    setParamInUrl(QUERY_KEYS.category, QUERY_VALUES.more, history, location)

    return ProductCategory.More
  }

  return ProductCategory.Rise
}

function getQueryValues(location: Location) {
  const params = new URLSearchParams(location.search)
  const categoryParam = params.get(QUERY_KEYS.category)
  const seriesParam = params.get(QUERY_KEYS.series)
  const skillParam = params.get(QUERY_KEYS.skill)
  const startTimeParam = params.get(QUERY_KEYS.start)
  const sortParam = params.get(QUERY_KEYS.sort)

  return { categoryParam, skillParam, seriesParam, sortParam, startTimeParam }
}

export interface HomeApiResponse {
  videos: VideoType[]
  upcomingWebinars: UpcomingWebinarType[]
  recordedWebinars: RecordedWebinarType[]
  series: Series[]
}

export default withRouter(function Home(props: HomeProps): JSX.Element {
  const { history, location, section } = props
  const [t] = useTranslation()
  const { getAccessToken, self } = useContext(AuthContext)
  const { showAdminControls, hidePastWebinars } = useContext(AdminContext)
  const userSubStatus = useContext(SubContext)
  const { data, error, loading } = useApi<HomeApiResponse>(
    getAccessToken,
    `${process.env.THREESIXTY_TRAINING_URI}/home`
  )

  const { categoryParam, skillParam, seriesParam, sortParam, startTimeParam } = getQueryValues(location)
  const currentProductCategory = getCurrentProductCategory(categoryParam, history, location)
  const selectedSkillLevel = getSkillLevelByQueryValue(skillParam)
  const selectedStartTime = getStartTimeByQueryValue(startTimeParam)
  const selectedRecordedWebinarSort = getRecordedWebinarSortByQueryValue(sortParam)
  const params = new URLSearchParams(history.location.search)
  const signUpId = params.get(UPCOMING_WEBINAR_SIGN_UP_ID)

  const { recordedWebinarsSearch, upcomingWebinarsSearch, videosSearch } = useSearchIndex(
    currentProductCategory,
    showAdminControls,
    hidePastWebinars,
    data
  )

  function removeSignUpParam() {
    params.delete(UPCOMING_WEBINAR_SIGN_UP_ID)
    history.replace({
      ...history.location,
      search: `?${params.toString()}`,
      state: history.location.state
    })
  }

  useEffect(() => {
    if (currentProductCategory === ProductCategory.More && section === Section.Videos) {
      history.replace(PATHS.webinars)
    }
  })

  useEffect(() => {
    if (signUpId && data && isLoggedIn(self)) {
      const { upcomingWebinars } = data
      const upcomingWebinar = upcomingWebinars.find(({ id }) => id === signUpId)
      const { recordedWebinar } = upcomingWebinar

      removeSignUpParam()

      if (!upcomingWebinar) {
        reportError(new Error('Cant find upcoming webinar for sign-up param'), undefined, { signUpId })

        return
      }

      track(
        events.registrationLinkClicked,
        {
          upcomingWebinarId: signUpId,
          title: upcomingWebinar.title,
          presenter: upcomingWebinar.presenter,
          recordedWebinarId: recordedWebinar.id,
          recordedWebinarTitle: recordedWebinar.title,
          rise: recordedWebinar.rise,
          storyline: recordedWebinar.storyline,
          more: recordedWebinar.more,
          seriesId: recordedWebinar.series.id,
          seriesTitle: recordedWebinar.series.title,
          skillLevel: recordedWebinar.skillLevel
        },
        getAccessToken,
        showAdminControls
      )

      openInNewTab(upcomingWebinar.registrationLink)
    }
  })

  function renderSelectedSection(): JSX.Element {
    const { recordedWebinars = [], series = [], upcomingWebinars = [], videos = [] } = data || {}
    const selectedSeries = series.find(s => s.title === seriesParam)
    const sectionProps = { currentProductCategory, loading: loading || userSubStatus.loading }
    const sortedSeries = sortSeries(series)
    if (section === Section.UpcomingWebinars) {
      return (
        <UpcomingWebinarsSection
          recordedWebinars={recordedWebinars}
          selectedSeries={selectedSeries}
          selectedSkillLevel={selectedSkillLevel}
          selectedStartTime={selectedStartTime}
          series={sortedSeries}
          upcomingWebinars={upcomingWebinars}
          upcomingWebinarsSearch={upcomingWebinarsSearch}
          {...sectionProps}
        />
      )
    }
    if (section === Section.RecordedWebinars) {
      return (
        <RecordedWebinarsSection
          recordedWebinars={recordedWebinars}
          recordedWebinarsSearch={recordedWebinarsSearch}
          selectedSeries={selectedSeries}
          selectedSkillLevel={selectedSkillLevel}
          series={sortedSeries}
          selectedSort={selectedRecordedWebinarSort}
          {...sectionProps}
        />
      )
    }

    return <VideosSection videos={videos} videosSearch={videosSearch} {...sectionProps} />
  }

  if (error) {
    reportError(error, 'Error fetching data for home page')

    return <GenericError />
  }

  return (
    <div className="home">
      <Header />
      <div className="content">
        <h2 className="title">{t('home.title')}</h2>
        <p className="subtitle">{t('home.subtitle')}</p>
        <div className="background-container">
          <ProductCategoryFilter currentProductCategory={currentProductCategory} selectedSection={section} />
        </div>
        <TabGroup currentProductCategory={currentProductCategory} selectedSection={section} />
        {renderSelectedSection()}
      </div>
      <Footer />
    </div>
  )
})
